import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import {
  FieldPath, FieldValues, UseFormReturn, useWatch,
} from 'react-hook-form';

import { formatBedDisplay, toNumberOrNull } from 'components/forms/BurnBedsGrid/burnBedsUtils';

import {
  RehabBedsFormFields,
  computeRehabBedTotal,
} from './rehabBedsUtils';

const EDITABLE_REHAB_KEYS: (keyof RehabBedsFormFields)[] = [
  'rehabLicensedInpatientAdultBeds',
  'rehabLicensedInpatientPediatricBeds',
];

const pickRehabBedFields = (
  source: Record<string, unknown> | undefined,
): Partial<RehabBedsFormFields> => {
  if (!source) return {};
  return {
    rehabLicensedInpatientAdultBeds: toNumberOrNull(source.rehabLicensedInpatientAdultBeds),
    rehabLicensedInpatientPediatricBeds: toNumberOrNull(
      source.rehabLicensedInpatientPediatricBeds,
    ),
    rehabLicensedInpatientTotalBeds: toNumberOrNull(source.rehabLicensedInpatientTotalBeds),
  };
};

const COLUMN_HEADERS = ['Adult', 'Pediatric', 'Total'] as const;

type Props<T extends FieldValues = FieldValues> = {
  form: UseFormReturn<T>;
  namePrefix?: string;
  editing?: boolean;
  disabled?: boolean;
  readOnlyValues?: Partial<RehabBedsFormFields>;
};

const RehabBedsGrid = <T extends FieldValues>({
  form,
  namePrefix = 'benchmark.',
  editing = true,
  disabled = false,
  readOnlyValues,
}: Props<T>) => {
  const prefix = namePrefix;
  const resolvePath = (key: keyof RehabBedsFormFields) => (
    prefix ? `${prefix}${key}` : key
  ) as FieldPath<T>;
  const watchRoot = prefix.endsWith('.') ? prefix.slice(0, -1) : prefix;

  const nestedWatch = useWatch({
    control: form.control,
    name: (watchRoot || undefined) as FieldPath<T>,
  });
  const flatWatch = useWatch({
    control: form.control,
    name: EDITABLE_REHAB_KEYS.map((key) => resolvePath(key)) as FieldPath<T>[],
  });
  const watched = pickRehabBedFields(
    watchRoot
      ? (nestedWatch as Record<string, unknown>)
      : Object.fromEntries(
        EDITABLE_REHAB_KEYS.map((key, index) => [key, flatWatch[index]]),
      ),
  );

  const displayValues = editing ? watched : readOnlyValues;
  const totals = computeRehabBedTotal(displayValues ?? {});

  useEffect(() => {
    if (!editing) return;

    const source = watchRoot
      ? pickRehabBedFields(
        form.getValues(watchRoot as FieldPath<T>) as Record<string, unknown>,
      )
      : pickRehabBedFields(
        Object.fromEntries(
          EDITABLE_REHAB_KEYS.map((key) => [
            key,
            form.getValues((prefix ? `${prefix}${key}` : key) as FieldPath<T>),
          ]),
        ),
      );

    const { rehabLicensedInpatientTotalBeds } = computeRehabBedTotal(source);
    form.setValue(
      (prefix ? `${prefix}rehabLicensedInpatientTotalBeds` : 'rehabLicensedInpatientTotalBeds') as FieldPath<T>,
      rehabLicensedInpatientTotalBeds as never,
      { shouldDirty: false, shouldValidate: false },
    );
  }, [
    editing,
    form,
    prefix,
    watchRoot,
    watched?.rehabLicensedInpatientAdultBeds,
    watched?.rehabLicensedInpatientPediatricBeds,
  ]);

  const getDisplayValue = (key: keyof RehabBedsFormFields, isTotal: boolean) => {
    if (isTotal) {
      return formatBedDisplay(
        totals.rehabLicensedInpatientTotalBeds
          ?? toNumberOrNull(displayValues?.[key]),
      );
    }
    return formatBedDisplay(toNumberOrNull(displayValues?.[key]));
  };

  const renderCell = (column: 'adult' | 'pediatric' | 'total') => {
    const keyMap = {
      adult: 'rehabLicensedInpatientAdultBeds',
      pediatric: 'rehabLicensedInpatientPediatricBeds',
      total: 'rehabLicensedInpatientTotalBeds',
    } as const;
    const key = keyMap[column];
    const isTotal = column === 'total';
    const display = getDisplayValue(key, isTotal);

    if (!editing) {
      return (
        <Form.Control
          type="text"
          readOnly
          disabled
          style={isTotal ? { backgroundColor: '#e2e6ea' } : undefined}
          className={isTotal ? undefined : 'bg-light'}
          value={display}
          tabIndex={-1}
        />
      );
    }

    if (isTotal) {
      return (
        <Form.Control
          type="number"
          readOnly
          style={{ backgroundColor: '#e2e6ea' }}
          value={display}
          tabIndex={-1}
        />
      );
    }

    return (
      <Form.Control
        type="number"
        inputMode="numeric"
        step={1}
        min={0}
        disabled={disabled}
        {...form.register(resolvePath(key), { valueAsNumber: true })}
      />
    );
  };

  return (
    <div className="rehab-beds-grid">
      <h5 className="fw-bold mb-3">Rehabilitation Beds</h5>
      <Row className="g-2 mb-2 align-items-end">
        <Col xs={12} sm={3} />
        {COLUMN_HEADERS.map((header) => (
          <Col key={header} xs={4} sm={3} className="text-muted small fw-semibold">
            {header}
          </Col>
        ))}
      </Row>
      <Row className="g-2 mb-3 align-items-center">
        <Col xs={12} sm={3} className="fw-semibold">
          Licensed Inpatient
        </Col>
        <Col xs={4} sm={3}>{renderCell('adult')}</Col>
        <Col xs={4} sm={3}>{renderCell('pediatric')}</Col>
        <Col xs={4} sm={3}>{renderCell('total')}</Col>
      </Row>
    </div>
  );
};

export default RehabBedsGrid;
